"use client";

import { type ReactElement, useCallback, useMemo, useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import AccountTypeForm from "./AccountTypeForm/AccountTypeForm";
import CommunicationDataForm from "./CommunicationDataForm/CommunicationDataForm";
import PreferencesForm from "./PreferencesForm/PreferencesForm";
import ConfirmationForm from "./ConfirmationForm/ConfirmationForm";
import ParentEmailForm from "./ParentEmailForm/ParentEmailForm";
import ParentVerificationForm from "./ParentVerificationForm/ParentVerificationForm";

import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import AppLogo from "@/components/Logo.tsx";
import LanguageSwitcher from "@/layouts/AuthLayout/components/LanguageSwitcher/LanguageSwitcher.tsx";
import PersonalDataForm from "@/features/auth/components/SignUpSteps/PersonalDataForm/PersonalDataForm.tsx";
import useAuthStore from "@/features/auth/store/useAuthStore";

export interface SignUpStep {
  title: string;
  summary: string;
  iconClassName: string;
  component: ReactElement;
}

export default function SignUpCard() {
  const [step, setStep] = useState<number>(0);
  const { t } = useTranslation(["auth", "common"]);
  const { onboardingData } = useAuthStore();

  const goToNextStep = useCallback(() => setStep((prev) => prev + 1), []);
  const goToPrevStep = useCallback(() => setStep((prev) => prev - 1), []);

  // Check if user is a student under 16
  const isStudentUnder16 = useMemo(() => {
    if (onboardingData.accountType !== "student" || !onboardingData.birthDate) return false;
    const bd = new Date(onboardingData.birthDate);
    const today = new Date();
    const threshold = new Date(today);

    threshold.setFullYear(today.getFullYear() - 16);

    return bd > threshold;
  }, [onboardingData.accountType, onboardingData.birthDate]);

  const steps: SignUpStep[] = useMemo(() => {
    const baseSteps: {
      title: string;
      summary: string;
      iconClassName: string;
      component: ReactElement;
    }[] = [
      {
        title: t("signUp.selectAccountType"),
        summary: t("signUp.stepSummaries.accountType"),
        iconClassName: "tabler--user-filled",
        component: <AccountTypeForm goToNextStep={goToNextStep} />,
      },
      {
        title: t("signUp.someOfPersonalData"),
        summary: t("signUp.stepSummaries.personalData"),
        iconClassName: "tabler--chart-donut-filled",
        component: <PersonalDataForm goToNextStep={goToNextStep} goToPrevStep={goToPrevStep} />,
      },
      {
        title: t("auth:signUp.someOfCommunicationData"),
        summary: t("signUp.stepSummaries.communicationData"),
        iconClassName: "tabler--message-filled",
        component: (
          <CommunicationDataForm goToNextStep={goToNextStep} goToPrevStep={goToPrevStep} />
        ),
      },
      {
        title: t("auth:signUp.someOfPreferences"),
        summary: t("signUp.stepSummaries.preferences"),
        iconClassName: "tabler--settings-filled",
        component: <PreferencesForm goToNextStep={goToNextStep} goToPrevStep={goToPrevStep} />,
      },
      {
        title: t("auth:signUp.confirmation"),
        summary: t("signUp.stepSummaries.confirmation"),
        iconClassName: "tabler--circle-check-filled",
        component: <ConfirmationForm goToNextStep={goToNextStep} goToPrevStep={goToPrevStep} />,
      },
    ];

    // Add parent consent steps for students under 16
    if (isStudentUnder16) {
      baseSteps.push(
        {
          title: t("auth:signUp.parentConsent.parentEmail"),
          summary: t("auth:signUp.stepSummaries.parentEmail"),
          iconClassName: "tabler--mail-filled",
          component: <ParentEmailForm goToNextStep={goToNextStep} goToPrevStep={goToPrevStep} />,
        },
        {
          title: t("auth:signUp.parentConsent.verification"),
          summary: t("auth:signUp.stepSummaries.parentVerification"),
          iconClassName: "tabler--shield-check-filled",
          component: <ParentVerificationForm goToPrevStep={goToPrevStep} />,
        },
      );
    }

    return baseSteps;
  }, [t, goToNextStep, goToPrevStep, isStudentUnder16]);

  const { component, title, summary } = steps[step];

  return (
    <div className="bg-background s:p-4 grid min-h-dvh place-items-center p-0">
      <div className="absolute top-10 right-10 z-[100] flex gap-2">
        <LanguageSwitcher />
      </div>
      <Card className="bg-foreground/50 relative z-10 flex min-h-[700px] w-full max-w-2xl overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl max-sm:min-h-dvh max-sm:rounded-none! lg:w-1/2 xl:w-fit">
        {/* Background Pattern */}
        <div className="absolute -bottom-20 -left-20 h-48 w-48 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 opacity-5" />
        <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-tr from-blue-400 to-cyan-500 opacity-5" />

        <div className="h-full">
          <CardHeader>
            <CardTitle className="flex flex-row justify-center">
              <AppLogo className="h-8" />
            </CardTitle>
          </CardHeader>
          <div className="h-full space-y-6">
            <div className="px-6">
              <div className="flex items-center justify-between py-5">
                <p className="text-accent-foreground text-2xl font-bold">{title}</p>
                <span className="text-muted-foreground text-sm font-medium">
                  {t("common:stepper", { step: step + 1, steps: steps.length })}
                </span>
              </div>
              <div className="mt-4 flex w-full gap-1">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={cn(
                      "h-1 w-full rounded-full transition-all duration-300",
                      index <= step ? "bg-accent-foreground/90" : "bg-muted/30",
                    )}
                  />
                ))}
              </div>
            </div>
            <CardContent className={"h-full"}>
              <motion.div
                key={step}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
                exit={{ opacity: 0, x: -20 }}
                initial={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-muted/20 flex h-8 w-8 min-w-[2rem] items-center justify-center rounded-2xl backdrop-blur-sm">
                      <span
                        className={`iconify text-accent-foreground text-xl ${steps[step].iconClassName}`}
                      />
                    </div>
                    <p className="text-muted-foreground text-sm">{summary}</p>
                  </div>
                </div>
                <div className="pt-4 sm:min-w-[500px]">{component}</div>
              </motion.div>
            </CardContent>
          </div>
        </div>
      </Card>
    </div>
  );
}
